import { useMemo } from "react";
import { AddableCourse, CourseLite, CourseSummary } from "../../types/course.response";
import { CourseSet } from "../../types/requirement.response";
import { getCourseKey } from "../../utils/courseUtils";
import { getCourseSetSlotInfo } from "../../utils/requirementResponseUtils";
import ChecklistCourseRow from "./ChecklistCourseRow";
import ChecklistEmptySlot from "./ChecklistEmptySlot";

interface ChecklistCourseSetSlotsProps {
    courseSet: CourseSet;
    coursesMap: Record<string, CourseSummary>;
    requirementId: string;
    onAdd: (course: AddableCourse) => void | Promise<void>;
    onDelete: (course: CourseLite) => void | Promise<void>;
    idPrefix: string;
    label?: string;
    onAddExtra?: (course: AddableCourse) => void | Promise<void>;
}

export default function ChecklistCourseSetSlots({
    courseSet,
    coursesMap,
    requirementId,
    onAdd,
    onDelete,
    idPrefix,
    label,
    onAddExtra,
}: ChecklistCourseSetSlotsProps) {
    const { appliedCourses, unappliedCourses, allCourses, emptySlotCount, isFulfilled } = useMemo(
        () => getCourseSetSlotInfo(courseSet, coursesMap),
        [courseSet, coursesMap]
    );

    return (
        <>
            {appliedCourses.map(({ course, isApplied }) => (
                <ChecklistCourseRow
                    key={getCourseKey(course)}
                    course={course}
                    isApplied={isApplied}
                    nodeId={courseSet.id}
                    requirementId={requirementId}
                    onDelete={onDelete}
                />
            ))}
            {Array.from({ length: emptySlotCount }, (_, i) => (
                <ChecklistEmptySlot
                    key={`${idPrefix}-${courseSet.id}-${i}`}
                    allCourses={allCourses}
                    onAdd={onAdd}
                    nodeId={courseSet.id}
                    dropId={`${idPrefix}-slot-${courseSet.id}-${i}`}
                    label={label}
                />
            ))}
            {unappliedCourses.map(({ course, isApplied }) => (
                <ChecklistCourseRow
                    key={getCourseKey(course)}
                    course={course}
                    isApplied={isApplied}
                    nodeId={courseSet.id}
                    requirementId={requirementId}
                    onDelete={onDelete}
                />
            ))}
            {isFulfilled && onAddExtra && (
                <ChecklistEmptySlot
                    allCourses={allCourses}
                    onAdd={onAddExtra}
                    nodeId={null}
                    dropId={`${idPrefix}-slot-${courseSet.id}-extra`}
                    compact
                />
            )}
        </>
    );
}
